import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Check, ChevronLeft, Zap } from 'lucide-react';
import { useContentAccess } from '../hooks/useContentAccess';
import { useAppStore } from '../store/appStore';
import AppLogo from '../components/EllieAvatar';

const FREE_FEATURES = [
  '50+ learning scenarios',
  'Age-matched content',
  'Unlimited child profiles',
  'Basic progress tracking',
  'Community access',
];

const PREMIUM_FEATURES = [
  'Everything in Free',
  'Practice Mode (swipeable sessions)',
  'Full progress dashboard',
  'Weekly Family Challenges + badges',
  'Parent Conversation Guides (PDF)',
  'Offline mode — save scenarios',
  'Early access to new content',
  'Ad-free experience',
];

export default function Pricing() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { isGuest, isPremium } = useContentAccess();
  const user = useAppStore((s) => s.user);
  const [billing, setBilling] = useState<'monthly' | 'annual'>('annual');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Pre-select billing if redirected back from onboarding
  useEffect(() => {
    const b = searchParams.get('billing');
    if (b === 'monthly' || b === 'annual') setBilling(b);
  }, []);

  const handleUpgrade = async () => {
    if (!user) {
      navigate('/onboarding');
      return;
    }
    // Guests must create a free account before paying
    if (isGuest) {
      sessionStorage.setItem('pendingBilling', billing);
      navigate('/onboarding');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const res = await fetch('/api/stripe/checkout', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          billing,
          userId: user.id,
          userEmail: user.email || undefined,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error ?? 'Checkout failed');
      window.location.href = data.url;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 via-sky-50 to-amber-50 pb-16">
      {/* Header */}
      <div className="px-4 pt-12 pb-8 text-center">
        <button
          onClick={() => navigate(-1)}
          className="absolute left-4 top-12 flex items-center gap-1 text-gray-500 hover:text-gray-700 text-sm font-semibold"
        >
          <ChevronLeft size={18} /> Back
        </button>

        <div className="flex justify-center mb-4">
          <AppLogo size={56} />
        </div>
        <h1 className="font-display text-3xl font-extrabold text-primary-800 mb-2">
          KidWise Premium
        </h1>
        <p className="text-gray-500 text-sm max-w-xs mx-auto leading-relaxed">
          Turn safety conversations into confident habits — with Practice Mode, parent guides, and weekly family challenges.
        </p>
      </div>

      <div className="px-4 max-w-lg mx-auto space-y-6">
        {isPremium && (
          <div className="bg-green-50 border border-green-200 rounded-2xl p-4 text-center">
            <p className="text-green-700 font-bold">✅ You're already on Premium — enjoy all the features!</p>
          </div>
        )}

        {/* Billing toggle */}
        {!isPremium && (
          <div className="flex items-center justify-center gap-1 bg-white rounded-2xl p-1 shadow-sm border border-gray-100">
            <button
              onClick={() => setBilling('monthly')}
              className={`flex-1 py-2 rounded-xl text-sm font-bold transition-all ${
                billing === 'monthly'
                  ? 'bg-primary-600 text-white shadow-sm'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              Monthly
            </button>
            <button
              onClick={() => setBilling('annual')}
              className={`flex-1 py-2 rounded-xl text-sm font-bold transition-all relative ${
                billing === 'annual'
                  ? 'bg-primary-600 text-white shadow-sm'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              Annual
              <span className="absolute -top-2 -right-1 bg-warm-400 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full">
                Save 33%
              </span>
            </button>
          </div>
        )}

        {/* Plan cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {/* Free card */}
          <div className="bg-white rounded-3xl p-5 shadow-card border border-gray-100">
            <div className="mb-4">
              <span className="text-xs font-bold text-gray-400 uppercase tracking-wide">Free</span>
              <div className="font-display text-3xl font-extrabold text-gray-800 mt-1">
                $0
              </div>
              <p className="text-gray-400 text-xs mt-0.5">forever</p>
            </div>
            <ul className="space-y-2 mb-5">
              {FREE_FEATURES.map((f) => (
                <li key={f} className="flex items-start gap-2 text-sm text-gray-600">
                  <Check size={15} className="text-green-500 flex-shrink-0 mt-0.5" />
                  {f}
                </li>
              ))}
            </ul>
            {isGuest ? (
              <button
                onClick={() => navigate('/onboarding')}
                className="w-full bg-primary-50 text-primary-700 py-3 rounded-2xl font-bold text-sm border border-primary-200 hover:bg-primary-100 transition-colors"
              >
                Get Started Free
              </button>
            ) : (
              <div className="w-full bg-gray-100 text-gray-400 py-3 rounded-2xl font-bold text-sm text-center">
                Your current plan
              </div>
            )}
          </div>

          {/* Premium card */}
          <motion.div
            initial={{ scale: 0.97 }}
            animate={{ scale: 1 }}
            className="bg-gradient-to-br from-primary-600 to-primary-800 rounded-3xl p-5 shadow-lg relative overflow-hidden"
          >
            <div className="absolute top-3 right-3">
              <span className="bg-warm-400 text-white text-[10px] font-bold px-2 py-1 rounded-full flex items-center gap-1">
                <Zap size={10} /> POPULAR
              </span>
            </div>
            <div className="mb-4">
              <span className="text-xs font-bold text-white/60 uppercase tracking-wide">Premium</span>
              <div className="font-display text-3xl font-extrabold text-white mt-1">
                {billing === 'annual' ? '$1.99' : '$2.99'}
                <span className="text-base font-semibold text-white/70">/mo</span>
              </div>
              <p className="text-white/60 text-xs mt-0.5">
                {billing === 'annual'
                  ? 'Billed $23.88/year · save 33%'
                  : 'Billed monthly · cancel anytime'}
              </p>
            </div>
            <ul className="space-y-2 mb-5">
              {PREMIUM_FEATURES.map((f) => (
                <li key={f} className="flex items-start gap-2 text-sm text-white/90">
                  <Check size={15} className="text-warm-400 flex-shrink-0 mt-0.5" />
                  {f}
                </li>
              ))}
            </ul>

            {isPremium ? (
              <div className="w-full bg-white/20 text-white py-3 rounded-2xl font-bold text-sm text-center">
                Active plan ✓
              </div>
            ) : (
              <button
                onClick={handleUpgrade}
                disabled={loading}
                className="w-full bg-warm-400 hover:bg-warm-500 text-white py-3 rounded-2xl font-bold text-sm shadow-md transition-colors disabled:opacity-60"
              >
                {loading ? 'Redirecting…' : 'Upgrade Now →'}
              </button>
            )}

            {error && (
              <p className="text-red-200 text-xs mt-2 text-center">{error}</p>
            )}
          </motion.div>
        </div>

        {/* Guarantee */}
        <div className="bg-white rounded-2xl p-4 flex items-start gap-3 shadow-sm border border-gray-100">
          <span className="text-2xl">🛡️</span>
          <div>
            <p className="font-bold text-gray-800 text-sm">14-day money-back guarantee</p>
            <p className="text-gray-400 text-xs mt-0.5 leading-relaxed">
              Not happy? Email us within 14 days of purchase for a full, no-questions-asked refund.
            </p>
          </div>
        </div>

        {/* Feature comparison */}
        <div className="bg-white rounded-3xl p-5 shadow-card">
          <h2 className="font-display font-extrabold text-gray-800 text-base mb-4">
            Everything you get with Premium
          </h2>
          <div className="space-y-3">
            {[
              { label: 'All 50+ free scenarios', free: true, premium: true },
              { label: '100+ premium deep-dive scenarios', free: false, premium: true },
              { label: 'Practice Mode (swipeable sessions)', free: false, premium: true },
              { label: 'Full progress & completion tracking', free: false, premium: true },
              { label: 'Weekly Family Challenges', free: '👁 View only', premium: true },
              { label: 'Parent Conversation Guides (PDF)', free: false, premium: true },
              { label: 'Offline mode', free: false, premium: true },
              { label: 'Community posting', free: true, premium: true },
            ].map(({ label, free, premium }) => (
              <div key={label} className="flex items-center gap-3">
                <span className="flex-1 text-sm text-gray-600">{label}</span>
                <span className="w-12 text-center text-sm">
                  {free === true ? '✅' : free === false ? '—' : <span className="text-xs text-gray-400">{free}</span>}
                </span>
                <span className="w-12 text-center text-sm">
                  {premium ? '✅' : '—'}
                </span>
              </div>
            ))}
            <div className="flex items-center gap-3 pt-1 border-t border-gray-100">
              <span className="flex-1" />
              <span className="w-12 text-center text-xs font-bold text-gray-400">Free</span>
              <span className="w-12 text-center text-xs font-bold text-primary-600">Premium</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
